import { useState, useEffect, useMemo, useRef } from 'react';
import { Modal, Alert, Form, Row, Col } from 'react-bootstrap';
import { FaInfoCircle, FaExclamationTriangle } from 'react-icons/fa';
import { toast } from 'sonner';

import TransactionHeader from './components/TransactionHeader';
import TransactionStepper from './components/TransactionStepper';
import TransactionTypeSelector from './components/TransactionTypeSelector';
import TransactionDetailsForm from './components/TransactionDetailsForm';
import TransactionDocuments from './components/TransactionDocuments';
import TransactionFooter from './components/TransactionFooter';

import './styles/PrivateTransactionModal.css';
import { useTransactionForm } from '../service/Hooks/Titres/useTransactionForm';
import { usePrivateTransaction } from '../service/Hooks/Titres/usePrivateTransaction';
import { TRANSACTION_TYPES } from '../service/Constants/transactionTypes';
import { TransactionConfirmationModal } from './ConfirmTransaction/ConfirmationModal';

const PrivateTransactionModal = ({ show, onHide, security, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false); // État pour le modal de confirmation
  
  const fileInputRef = useRef(null);

  const { loading: transactionLoading, submitTransaction, validateTransaction } = usePrivateTransaction();
  const { formData, updateField, resetForm, handleFileUpload, removeDocument } = useTransactionForm(security);

  // Mettre à jour le type sélectionné
  useEffect(() => {
    if (formData.transactionType) {
      const type = TRANSACTION_TYPES.find(t => t.value === formData.transactionType);
      setSelectedType(type);
    } else {
      setSelectedType(null);
    }
  }, [formData.transactionType]);

  // Réinitialiser quand le modal s'ouvre
  useEffect(() => {
    if (show) {
      resetForm();
      setStep(1);
      setErrors({});
      setValidationErrors({});
      setSuccess(false);
      setSelectedType(null);
      setIsLoading(false);
      setShowConfirmation(false);
    }
  }, [show, resetForm]);

  // Valider l'étape en cours
  const validateStep = () => {
    const newErrors = {};
    
    if (step === 1 && !formData.transactionType) {
      newErrors.transactionType = 'Veuillez sélectionner un type de transaction';
    }
    
    if (step === 2) {
      validateStep2(newErrors);
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = (newErrors) => {
    if (!formData.quantity || formData.quantity <= 0) {
      newErrors.quantity = 'Veuillez saisir une quantité positive';
    }

    if (!formData.accountCategory) {
      newErrors.accountCategory = 'Veuillez sélectionner une catégorie de compte';
    }

    if (selectedType?.requires.fromAccount && !formData.fromAccountId) {
      newErrors.fromAccountId = 'Compte source requis pour ce type de transaction';
    }

    if (selectedType?.requires.toAccount && !formData.toAccountId) {
      newErrors.toAccountId = 'Compte destination requis pour ce type de transaction';
    }

    if (selectedType?.requires.conversion && !formData.conversionSecurityId) {
      newErrors.conversionSecurityId = 'Titre de conversion requis';
    }

    // Validation des dates (seulement si fournies)
    if (formData.dateOperation) {
      const operationDate = new Date(formData.dateOperation);
      if (operationDate > new Date()) {
        newErrors.dateOperation = 'La date d\'opération ne peut pas être dans le futur';
      }
    }

    if (formData.dateCredit && formData.dateOperation) {
      const creditDate = new Date(formData.dateCredit);
      const operationDate = new Date(formData.dateOperation);
      if (creditDate < operationDate) {
        newErrors.dateCredit = 'La date de crédit ne peut pas être antérieure à la date d\'opération';
      }
    }
  };

  const handleNext = () => {
    if (validateStep()) {
      setStep(prev => Math.min(prev + 1, 3));
      setErrors({});
    }
  };

  const handlePrevious = () => {
    setStep(prev => Math.max(prev - 1, 1));
    setErrors({});
  };

  // Fonction pour afficher le modal de confirmation
  const showValidationModal = () => {
    // Valider toutes les étapes
    const stepValidation = validateStep();
    if (!stepValidation) {
      const errorMessages = Object.values(errors).join(', ');
      toast.error(`Veuillez corriger les erreurs: ${errorMessages}`);
      return;
    }

    // Valider la transaction complète
    const validationErrors = validateTransaction(formData);
    
    if (Object.keys(validationErrors).length > 0) {
      setValidationErrors(validationErrors);
      const errorMessages = Object.values(validationErrors).join(', ');
      toast.error(`Erreurs de validation: ${errorMessages}`);
      return;
    }

    setShowConfirmation(true);
  };

  // Fonction pour exécuter la soumission après confirmation
  const handleSubmitConfirmed = async () => {
    setShowConfirmation(false);
    
    try {
      setIsLoading(true);
      
      const result = await submitTransaction(formData);
      
      if (result.success) {
        setSuccess(true);
        toast.success('Transaction créée avec succès!');
        setTimeout(() => {
          handleClose();
          onSuccess?.();
        }, 2000);
      } else {
        setValidationErrors({ general: result.error });
        toast.error(`Erreur: ${result.error}`);
      }
    } catch (error) {
      // console.error('Erreur complète:', error);
      const errorMessage = error.message || 'Erreur inconnue';
      setValidationErrors({ general: errorMessage });
      toast.error(`Erreur: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    setStep(1);
    setErrors({});
    setValidationErrors({});
    setSuccess(false);
    setSelectedType(null);
    setIsLoading(false);
    setShowConfirmation(false);
    onHide();
  };

  const handleTypeSelect = (type) => {
    updateField('transactionType', type);
    
    // Réinitialiser les champs dépendants du type
    if (type !== selectedType?.value) {
      updateField('accountCategory', '');
      updateField('fromAccountId', null);
      updateField('toAccountId', null);
      updateField('conversionSecurityId', null);
      updateField('fromAccountCategory', '');
    }
  };

  // Gestion spécifique des fichiers
  const handleFileUploadWrapper = (files) => {
    if (!files || files.length === 0) return;
    
    const newFiles = Array.from(files);
    
    // Vérifier la taille des fichiers (max 10MB)
    const oversizedFiles = newFiles.filter(file => file.size > 10 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      toast.error(`${oversizedFiles.length} fichier(s) dépassent 10MB`);
      return;
    }
    
    // Vérifier le nombre total de fichiers
    if (formData.documents.length + newFiles.length > 20) {
      toast.error('Maximum 20 fichiers autorisés');
      return;
    }
    
    handleFileUpload(files);
    toast.success(`${newFiles.length} fichier(s) ajouté(s)`);
  };

  // Calculer si l'étape est valide
  const isStepValid = useMemo(() => {
    if (step === 1) return formData.transactionType;
    if (step === 2) {
      const tempErrors = {};
      validateStep2(tempErrors);
      return Object.keys(tempErrors).length === 0;
    }
    return true; // Étape 3 (documents) est toujours valide
  }, [step, formData, selectedType]);

  // Loading overlay
  if (isLoading && !transactionLoading) {
    return (
      <Modal show={show} onHide={handleClose} size="xl" centered>
        <Modal.Body className="text-center p-5">
          <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
          <h5>Traitement en cours...</h5>
          <p className="text-muted">Veuillez patienter pendant la création de la transaction</p>
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <>
      {/* Modal de confirmation */}
      <TransactionConfirmationModal
        show={showConfirmation}
        onHide={() => setShowConfirmation(false)}
        onConfirm={handleSubmitConfirmed}
        transactionType={selectedType?.label || formData.transactionType}
        quantity={formData.quantity}
        securityName={security?.name}
        isLoading={transactionLoading || isLoading}
      />
      
      {/* Modal principal */}
      <Modal 
        show={show} 
        onHide={handleClose} 
        centered 
        backdrop="static"
        dialogClassName="transaction-modal"
      >
        <TransactionHeader 
          security={security} 
          onClose={handleClose} 
        />
        
        <Modal.Body className="p-4">
          <div className="transaction-guide mb-4">
            <Alert variant="info" className="mb-0">
              <FaInfoCircle className="me-2" />
              <strong>Guide de transaction :</strong> Suivez les 3 étapes pour créer votre transaction
            </Alert>
          </div>

          <TransactionStepper 
            currentStep={step} 
            totalSteps={3} 
          />

          {success && (
            <Alert variant="success" className="mb-3 animate-fade-in">
              <FaInfoCircle className="me-2" />
              Transaction créée avec succès ! Redirection dans 2 secondes...
            </Alert>
          )}

          {/* Afficher les erreurs de validation détaillées */}
          {Object.keys(validationErrors).length > 0 && (
            <Alert variant="danger" className="mb-3">
              <FaExclamationTriangle className="me-2" />
              <div>
                <strong>Erreurs de validation :</strong>
                <ul className="mb-0 mt-2">
                  {Object.entries(validationErrors).map(([field, error]) => (
                    <li key={field}>
                      {error}
                    </li>
                  ))}
                </ul>
              </div>
            </Alert>
          )}

          {/* Guide contextuel */}
          {step === 1 && (
            <Alert variant="light" className="mb-3 border">
              <strong>Étape 1 - Type de transaction :</strong> Sélectionnez le type d'opération que vous souhaitez effectuer. 
              Chaque type a des règles spécifiques et des documents requis.
            </Alert>
          )}

          {step === 2 && selectedType && (
            <Alert variant="light" className="mb-3 border">
              <strong>Étape 2 - Détails de la transaction :</strong> Renseignez les informations spécifiques à votre transaction.
              {selectedType.requires.fromAccount && " Vous devez sélectionner un compte source."}
              {selectedType.requires.toAccount && " Vous devez sélectionner un compte destination."}
              {selectedType.requires.conversion && " Vous devez sélectionner un titre de conversion."}
              <br />
              <small className="text-muted">Les dates sont optionnelles.</small>
            </Alert>
          )}

          {step === 3 && selectedType && (
            <Alert variant="light" className="mb-3 border">
              <strong>Étape 3 - Documents justificatifs :</strong> 
              {selectedType.value === 'Emission' ? ' Pour une émission, le prospectus est obligatoire.' : ''}
              {selectedType.value === 'Mutation' ? ' Pour une mutation, l\'acte de mutation est requis.' : ''}
              {selectedType.value === 'Sequestre' ? ' Pour un séquestre, la décision de justice est obligatoire.' : ''}
              {!['Emission', 'Mutation', 'Sequestre'].includes(selectedType.value) ? ' Les documents sont recommandés pour la traçabilité mais optionnels.' : ''}
              <br />
              <small className="text-muted">Vous pouvez ajouter autant de documents que nécessaire (max 20 fichiers).</small>
            </Alert>
          )}

          {/* Contenu de l'étape */}
          {step === 1 && (
            <TransactionTypeSelector
              transactionTypes={TRANSACTION_TYPES}
              selectedType={formData.transactionType}
              onSelect={handleTypeSelect}
              error={errors.transactionType}
            />
          )}

          {step === 2 && selectedType && (
            <div className="step-content">
              <h5 className="mb-4">Détails de la transaction</h5>

              <TransactionDetailsForm
                formData={formData}
                errors={errors}
                selectedType={selectedType}
                onFieldChange={updateField}
              />
            </div>
          )}

          {step === 3 && selectedType && (
            <TransactionDocuments
              documents={formData.documents}
              selectedType={selectedType}
              onFileUpload={handleFileUploadWrapper}
              onRemoveDocument={removeDocument}
            />
          )}
        </Modal.Body>

        <TransactionFooter
          step={step}
          totalSteps={3}
          loading={transactionLoading || isLoading}
          success={success}
          onPrev={handlePrevious}
          onNext={handleNext}
          onSubmit={showValidationModal} // Utiliser showValidationModal au lieu de handleSubmit
          onCancel={handleClose}
          isStepValid={isStepValid}
        />
      </Modal>
    </>
  );
};

export default PrivateTransactionModal;